import React, { useState, useMemo, useEffect, useCallback } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useTranslation, Trans } from 'react-i18next';
import { useProyectos } from '../hooks/useProyectos';
import api from '../api/axios';
import CancelDialog from './CancelDialog';
import moralejaImg from '../imagenes/Centros/moraleja.png';
import pozueloImg from '../imagenes/Centros/pozuelo.png';
import plazacastillaImg from '../imagenes/Centros/plazacastilla.png';
import otherImg from '../imagenes/Centros/other.png';
import { TeacherDelayButton } from './TicketButtons';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { Tooltip } from '@mui/material';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Stack,
  Typography,
  Button,
  CircularProgress,
  Box,
  Chip,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  Edit,
  ContentCopy,
  Cancel as CancelIcon,
  Person as PersonIcon,
  Link as LinkIcon,
} from '@mui/icons-material';

import { useSnackbar } from 'notistack';
import { useReservaLogs } from '../hooks/useReservasLogs';
import { useClientes, Cliente } from '../hooks/useClientes';
import { useCentros } from '../hooks/useCentros';
import { useAsignaturas } from '../hooks/useAsignaturas';
import { useFormatos } from '../hooks/useFormatos';
import { useAuth } from '../context/AuthContext';
import { useFirmas } from '../hooks/useFirmas';
import OneMinutePaperSection from './OneMinutePaperSection';

import FirmaDialog from './FirmaDialog';
import ClienteDialog from './ClienteDialog';

/* ⬇️ Hooks de aulas (tu API) */
import { useAula, usePlanificadorCentro } from '../api/aulas';

/* ⬇️ Botones de incidencias del profesor */
import { TeacherAbsenceButton } from './TicketButtons';
import { StaffDelayButton } from './TicketButtons';

/* Iconos para RETRASO y AUSENCIA DEL ALUMNO*/
import { StudentAbsenceButton } from './TicketButtons';
import { StudentDelayButton } from './TicketButtons';

/* ───────── Dayjs plugins ───────── */
dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);
const TZ = 'Europe/Madrid';

/* ───────── tipos auxiliares ───────── */
type Dic = {
  cliente: Record<number, string>;
  centro: Record<number, string>;
  asign: Record<number, string>;
  formato: Record<number, string>;
  proy: Record<number, string>;
};

interface Props {
  open: boolean;
  reserva?: any;
  onClose: () => void;
  onEdit?: (row: any) => void;
  onDuplicate?: (row: any) => void;
  onAmpliar?: (row: any) => void;
}

/* ═════════════════════════════════════════════════════════════ */
export default function ReservaDialog({ open, reserva, onClose, onEdit, onDuplicate }: Props) {
  const { t, i18n } = useTranslation<'translation'>();
  const { enqueueSnackbar } = useSnackbar();

  /* ---------- rol ---------- */
  const { user } = useAuth();
  const isProfesor = user?.roles?.includes('profesor') ?? false;
  const isCliente = user?.roles?.includes('cliente') ?? false;
  const isConsultor = user?.roles?.includes('consultor') ?? false;
  const isStaff = !isProfesor && !isCliente;

  /* ---------- estado local ---------- */
  const [tab, setTab] = useState(0);
  const [dlgFirma, setDlgFirma] = useState(false);
  const [dlgCancel, setDlgCancel] = useState(false);

  /* ====== Enlaces de firma ====== */
  const [linksByAlumno, setLinksByAlumno] = useState<Record<number, string>>({});
  const [loadingGen, setLoadingGen] = useState<Record<number, boolean>>({});

  /* ====== Formularios de no-firma ====== */
  const [formLinksByAlumno, setFormLinksByAlumno] = useState<Record<number, string>>({});
  const [loadingForm, setLoadingForm] = useState<Record<number, boolean>>({});

  /* ====== Estado de ventanas (API ligera) ====== */
  const [winByAlumno, setWinByAlumno] = useState<
    Record<
      number,
      {
        hasStarted: boolean;
        signOpen: boolean;
        signReason: 'start_20' | 'student_delay_30' | null;
        signUntil: string | null;
        formOpen: boolean;
      }
    >
  >({});

  const [clienteSel, setClienteSel] = useState<Cliente | null>(null);
  const [loadingCli, setLoadingCli] = useState(false);

  /* ---------- hooks de datos ---------- */
  const { data: firmas = [] } = useFirmas(reserva?.id);
  const yaFirmo = firmas.some((f: any) => f.idUsuario === user?.id);
  const firmadas = firmas.length;
  const faltan = reserva?.firmasRequeridas ? Math.max(reserva.firmasRequeridas - firmadas, 0) : 0;

  const { data: logs = [], isFetching } = useReservaLogs(
    isStaff && tab === 1 ? reserva?.id : undefined,
  );

  const { rows: clientes = [], update } = useClientes();
  const { rows: centros = [] } = useCentros();
  const { rows: asignaturas = [] } = useAsignaturas();
  const { rows: formatos = [] } = useFormatos();
  const { rows: proyectos = [] } = useProyectos();

  const dic = useMemo<Dic>(
    () => ({
      cliente: Object.fromEntries(clientes.map((c) => [c.id, c.nombreCompleto])),
      centro: Object.fromEntries(centros.map((c) => [c.id, c.nombre])),
      asign: Object.fromEntries(asignaturas.map((a) => [a.id, a.nombre])),
      formato: Object.fromEntries(formatos.map((f) => [f.id, f.nombreFormato])),
      proy: Object.fromEntries(proyectos.map((p) => [p.id, p.nombre])),
    }),
    [clientes, centros, asignaturas, formatos, proyectos],
  );

  /* ====== Tutoría Aplicada (solo PROFESOR) ====== */
  const [taUrl, setTaUrl] = useState<string | null>(null);
  const [taLoading, setTaLoading] = useState(false);

  /* ⚠️ Usar la reserva combinada (prop + fresh) siempre que sea posible */
  const [reservaFresh, setReservaFresh] = useState<any | null>(null);

  /* -------- helper para refrescar detalle -------- */
  const fetchReservaFresh = useCallback(async () => {
    if (!reserva?.id) return;
    try {
      const { data } = await api.get(`/reservas/${reserva.id}`);
      setReservaFresh(data ?? null);
    } catch {
      /* silencioso */
    }
  }, [reserva?.id]);

  /* Refresco ligero al abrir (y cuando cambia el id) */
  useEffect(() => {
    let cancel = false;
    setReservaFresh(null);
    if (!open || !reserva?.id) return;

    (async () => {
      try {
        const { data } = await api.get(`/reservas/${reserva.id}`);
        if (!cancel) setReservaFresh(data ?? null);
      } catch {
        /* silencioso */
      }
    })();

    return () => {
      cancel = true;
    };
  }, [open, reserva?.id]);

  // Combinar prop + refresco
  const reservaEff = useMemo(
    () => ({ ...(reserva ?? {}), ...(reservaFresh ?? {}) }),
    [reserva, reservaFresh],
  );

  /* ---------- Resolver enlaces de tutoría aplicada ---------- */
  // Centro (ahora desde la reserva "efectiva")
  const centroId: number | undefined =
    (reservaEff as any)?.idCentro ??
    (reservaEff as any)?.centroId ??
    (reservaEff as any)?.id_centro;

  // idAsignatura: usa id directo; si no, intenta deducir por nombre
  const asignaturaId: number | undefined = useMemo(() => {
    const direct =
      (reservaEff as any)?.idAsignatura ??
      (reservaEff as any)?.asignaturaId ??
      (reservaEff as any)?.id_asignatura;
    if (direct) return Number(direct);

    if ((reservaEff as any)?.nombreAsignatura && Array.isArray(asignaturas)) {
      const hit = asignaturas.find(
        (a: any) =>
          a?.nombre === (reservaEff as any).nombreAsignatura ||
          a?.nombre_asignatura === (reservaEff as any).nombreAsignatura,
      );
      return hit?.id;
    }
    return undefined;
  }, [
    (reservaEff as any)?.idAsignatura,
    (reservaEff as any)?.asignaturaId,
    (reservaEff as any)?.id_asignatura,
    (reservaEff as any)?.nombreAsignatura,
    asignaturas,
  ]);

  useEffect(() => {
    let cancelled = false;

    async function fetchTaUrl() {
      if (!open || !isProfesor || !centroId || !asignaturaId) {
        setTaUrl(null);
        return;
      }
      setTaLoading(true);
      try {
        const res = await api.get('/tutorias-aplicadas/links/resolve', {
          params: { centroId, asignaturaId },
          validateStatus: (s) => (s >= 200 && s < 300) || s === 404,
        });
        if (cancelled) return;
        setTaUrl(res.status === 404 ? null : (res.data?.url ?? null));
      } finally {
        if (!cancelled) setTaLoading(false);
      }
    }

    fetchTaUrl();
    return () => {
      cancelled = true;
    };
  }, [open, isProfesor, centroId, asignaturaId]);

  /* ────────────────────────────────────────────────
     AULA: id directo o fallback por planificador
  ──────────────────────────────────────────────── */
  // a) IDs posibles que pueda traer la reserva (usar reservaEff)
  const aulaIdDirect: number | undefined =
    (reservaEff as any)?.id_aula ?? (reservaEff as any)?.idAula ?? (reservaEff as any)?.aulaId;

  // b) Datos para el planificador (día/centro) — usar fecha de la reserva efectiva
  const fechaStr = dayjs((reservaEff as any)?.fecha)
    .tz(TZ)
    .format('YYYY-MM-DD');

  // c) Planificador (añadimos refetch para poder refrescar bajo demanda)
  const { data: planData, refetch: refetchPlan } = usePlanificadorCentro(centroId ?? 0, fechaStr);

  // d) Fallback: buscar en el planificador (si el detalle no trae id_aula)
  const aulaIdFromPlanner: number | undefined = useMemo(() => {
    const r = planData?.reservas?.find?.((x: any) => Number(x.id) === Number(reservaEff?.id));
    return r && r.aula_estado === 'ASIGNADA' && r.id_aula ? Number(r.id_aula) : undefined;
  }, [planData, reservaEff?.id]);

  /* NUEVO: detectar estado de aula desde planificador para mostrar texto amigable */
  const aulaEstadoFromPlanner: string | undefined = useMemo(() => {
    const r = planData?.reservas?.find?.((x: any) => Number(x.id) === Number(reservaEff?.id));
    return r?.aula_estado;
  }, [planData, reservaEff?.id]);

  // e) Id efectivo → prioriza planificador (más fresco) y luego el dato directo
  const aulaIdEffective: number | undefined = aulaIdFromPlanner ?? aulaIdDirect;

  // f) Cargar el aula (nombre/capacidad)
  const { data: aula } = useAula(aulaIdEffective);

  // g) Estado efectivo → también prioriza el planificador
  const aulaEstadoEff: string | undefined =
    aulaEstadoFromPlanner ??
    (reservaEff as any)?.aulaEstado ??
    (reservaEff as any)?.estadoAula ??
    (reservaEff as any)?.aula_estado ??
    (reservaEff as any)?.estado_aula;

  /* NUEVO: motivo legible si no hay aula */
  const aulaMotivo: string | undefined =
    aulaEstadoEff === 'NO_REQUIERE'
      ? 'No requiere aula'
      : aulaEstadoEff === 'PENDIENTE'
        ? 'No hay aulas libres'
        : undefined;

  // Nombre de aula para el chip (solo nombre, sin capacidad). Si no hay id y hay motivo, mostrarlo.
  const aulaNombreEfectivo: string = aulaIdEffective
    ? aula
      ? aula.nombre
      : ((reservaEff as any)?.aulaNombre ??
        (reservaEff as any)?.nombreAula ??
        `#${aulaIdEffective}`)
    : (aulaMotivo ?? (reservaEff as any)?.aulaNombre ?? (reservaEff as any)?.nombreAula ?? '—');

  /* ---------- detectar formato online ---------- */
  const formatoNombreEff: string | undefined =
    (reservaEff as any)?.nombreFormato ??
    ((reservaEff as any)?.idFormato
      ? dic.formato[(reservaEff as any).idFormato]
      : (reservaEff as any)?.formatoId
        ? dic.formato[(reservaEff as any).formatoId]
        : (reservaEff as any)?.id_formato
          ? dic.formato[(reservaEff as any).id_formato]
          : undefined);
  const isFormatoOnline = (formatoNombreEff ?? '').toLowerCase().includes('online');

  /* ⬇️⬇️⬇️ Tick de tiempo mientras el diálogo esté abierto ⬇️⬇️⬇️ */
  const [nowTz, setNowTz] = useState(dayjs().tz(TZ));
  useEffect(() => {
    if (!open) return;
    const id = setInterval(() => setNowTz(dayjs().tz(TZ)), 30_000); // 30s
    return () => clearInterval(id);
  }, [open]);
  /* ⬆️⬆️⬆️ FIN ⬆️⬆️⬆️ */

  /* ---------- helpers de horario ---------- */
  const parseHHmm = (hm?: string | number | null) => {
    const m = String(hm ?? '')
      .trim()
      .match(/^(\d{1,2}):(\d{2})/);
    return m ? { hh: m[1].padStart(2, '0'), mm: m[2] } : null;
  };

  const horaIniRaw =
    (reservaEff as any)?.horaInicio ??
    (reservaEff as any)?.hora_inicio ??
    (reservaEff as any)?.horaDesde ??
    (reservaEff as any)?.hora_desde ??
    null;

  const horaFinRaw =
    (reservaEff as any)?.horaFin ??
    (reservaEff as any)?.hora_fin ??
    (reservaEff as any)?.horaFinal ??
    (reservaEff as any)?.hora_final ??
    (reservaEff as any)?.horaHasta ??
    (reservaEff as any)?.hora_hasta ??
    null;

  const hIni = parseHHmm(horaIniRaw);
  const hFin = parseHHmm(horaFinRaw);

  const start = hIni ? dayjs.tz(`${fechaStr} ${hIni.hh}:${hIni.mm}`, 'YYYY-MM-DD HH:mm', TZ) : null;
  const end = hFin ? dayjs.tz(`${fechaStr} ${hFin.hh}:${hFin.mm}`, 'YYYY-MM-DD HH:mm', TZ) : null;

  const hasStarted = start ? !nowTz.isBefore(start) : true;
  const startPlus20 = start ? start.add(20, 'minute') : null;
  const inStart20Window =
    !!startPlus20 && hasStarted && (nowTz.isBefore(startPlus20) || nowTz.isSame(startPlus20));
  const canTimeWindow = hasStarted && inStart20Window;
  const canTeacherDelay = start ? !nowTz.isBefore(start.subtract(4, 'hour')) : false;

  /* ---------- alumnos (ids) — SIEMPRE usuarios.id ---------- */
  const titularUsrId =
    (reservaEff as any)?.idClienteUsr ?? (reservaEff as any)?.id_cliente_usr ?? undefined;

  const alumnosIds: number[] = useMemo(() => {
    // Esta lista debe venir ya en usuarios.id; si viene vacía, añadimos el titularUsrId
    const arr = Array.isArray((reservaEff as any)?.alumnosIds)
      ? (reservaEff as any)!.alumnosIds
      : [];
    const nums = arr.map((x: any) => Number(x)).filter((n: number) => Number.isFinite(n));
    const set = new Set<number>(nums);
    if (titularUsrId != null) set.add(Number(titularUsrId));
    return Array.from(set);
  }, [(reservaEff as any)?.alumnosIds, titularUsrId]);

  const alumnosIdsKey = useMemo(() => alumnosIds.join(','), [alumnosIds]);

  // Filtrar ids inválidos/0 — algunos backends devuelven 0 cuando no hay titular.
  const alumnosIdsEffective: number[] = useMemo(
    () => alumnosIds.filter((id) => Number.isFinite(id) && Number(id) > 0),
    [alumnosIds],
  );
  const alumnosIdsKeyEffective = useMemo(
    () => alumnosIdsEffective.join(','),
    [alumnosIdsEffective],
  );

  /* ---------- sincronizar ventanas desde backend ---------- */
  const fetchWindows = useCallback(async () => {
    if (!open || !reservaEff?.id || alumnosIdsEffective.length === 0) return;
    try {
      const responses = await Promise.all(
        alumnosIdsEffective.map(async (id) => {
          try {
            const { data } = await api.get(`/reservas/${reservaEff.id}/firmas/window`, {
              params: { alumnoId: id },
              validateStatus: (s) => s >= 200 && s < 500,
            });
            return [id, data] as [number, any];
          } catch {
            return null;
          }
        }),
      );
      const map: Record<number, any> = {};
      for (const pair of responses) {
        if (pair) map[pair[0]] = pair[1];
      }
      setWinByAlumno(map);
    } catch {
      /* silencioso */
    }
  }, [open, reservaEff?.id, alumnosIds]);

  useEffect(() => {
    if (!open || !reservaEff?.id) return;
    fetchWindows();
    const id = setInterval(fetchWindows, 30_000);
    return () => clearInterval(id);
  }, [open, reservaEff?.id, alumnosIdsKeyEffective, fetchWindows]);

  const canSignForAlumno = (alumnoId: number) => {
    if (!hasStarted) return false;
    const w = winByAlumno[alumnoId];
    if (w && typeof w.signOpen === 'boolean') return w.signOpen;
    return inStart20Window; // fallback local si aún no llegó estado
  };

  const canFormForAlumno = (alumnoId: number) => {
    if (!hasStarted) return false;
    return !canSignForAlumno(alumnoId);
  };
  const canAlumnoActions = (alumnoId: number) =>
    canSignForAlumno(alumnoId) || canFormForAlumno(alumnoId);

  /* ---------- acciones (repuestos que faltaban) ---------- */
  const generarEnlaceAlumno = async (alumnoId: number) => {
    if (!reservaEff?.id) return;
    try {
      setLoadingGen((p) => ({ ...p, [alumnoId]: true }));
      const { data } = await api.post(`/reservas/${reservaEff.id}/firmas/links`, {
        incluirProfesor: false,
        alumnosIds: [alumnoId],
        modo: 'presencial',
        venceHoras: 72,
      });
      const hit = (Array.isArray(data) ? data : []).find(
        (x: any) => Number(x.idUsuario) === Number(alumnoId),
      );
      if (!hit?.url) throw new Error('No se obtuvo URL');
      setLinksByAlumno((p) => ({ ...p, [alumnoId]: hit.url }));
      enqueueSnackbar('Enlace de firma generado', { variant: 'success' });
    } catch (e: any) {
      const msg = e?.response?.data?.message ?? e?.message ?? 'No se pudo generar el enlace';
      enqueueSnackbar(String(msg), { variant: 'error' });
    } finally {
      setLoadingGen((p) => ({ ...p, [alumnoId]: false }));
    }
  };

  const copiarEnlaceAlumno = async (alumnoId: number) => {
    const url = linksByAlumno[alumnoId];
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      enqueueSnackbar('Enlace copiado al portapapeles', { variant: 'success' });
    } catch {
      enqueueSnackbar('No se pudo copiar el enlace', { variant: 'error' });
    }
  };

  const generarFormularioAlumno = async (alumnoId: number) => {
    if (!reservaEff?.id) return;
    try {
      setLoadingForm((p) => ({ ...p, [alumnoId]: true }));
      const { data } = await api.post(`/reservas/${reservaEff.id}/firmas/links`, {
        tipo: 'FORM',
        incluirProfesor: false,
        alumnosIds: [alumnoId],
      });
      const hit = (Array.isArray(data) ? data : []).find(
        (x: any) => Number(x.idUsuario) === Number(alumnoId),
      );
      if (!hit?.url) throw new Error('No se obtuvo URL');
      setFormLinksByAlumno((p) => ({ ...p, [alumnoId]: hit.url }));
      enqueueSnackbar('Formulario generado', { variant: 'success' });
    } catch (e: any) {
      const msg = e?.response?.data?.message ?? e?.message ?? 'No se pudo generar el formulario';
      enqueueSnackbar(String(msg), { variant: 'error' });
    } finally {
      setLoadingForm((p) => ({ ...p, [alumnoId]: false }));
    }
  };

  const copiarFormularioAlumno = async (alumnoId: number) => {
    const url = formLinksByAlumno[alumnoId];
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      enqueueSnackbar('Formulario copiado al portapapeles', {
        variant: 'success',
      });
    } catch {
      enqueueSnackbar('No se pudo copiar el formulario', { variant: 'error' });
    }
  };

  /* ---------- helpers UI ---------- */
  const fmtFecha = (d?: string, hIni?: string, hFin?: string) =>
    d ? `${dayjs(d).locale(i18n.language).format('dddd D MMMM YYYY')} ${hIni} – ${hFin}` : '—';

  const alumnosNombres = useMemo(
    () => alumnosIdsEffective.map((id) => dic.cliente[id] ?? `#${id}`),
    [alumnosIdsEffective, dic.cliente],
  );

  const ocupacion =
    (reservaEff as any)?.numAlumnos > 1
      ? `${alumnosNombres.length} / ${(reservaEff as any)?.numAlumnos}`
      : undefined;

  const showInternos = isStaff;

  const estadoLegible =
    (reservaEff as any)?.estado ??
    (reservaEff as any)?.estado_label ??
    (reservaEff as any)?.estadoDescripcion ??
    (reservaEff as any)?.estadoCode ??
    (reservaEff as any)?.estado_code ??
    '—';

  useEffect(() => {
    const handler = (e: Event) => {
      try {
        const detail = (e as CustomEvent).detail || {};
        const id = Number(detail.id ?? detail.reservaId);
        if (id && Number(reservaEff?.id) === id) {
          // refrescar planner + detalle
          refetchPlan?.();
          fetchReservaFresh();
        }
      } catch {
        /* no-op */
      }
    };

    window.addEventListener('reserva:updated', handler);
    return () => window.removeEventListener('reserva:updated', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservaEff?.id, refetchPlan, fetchReservaFresh]);

  /* ───── botón de auto-asignación para profesor (condición: formato Online) ───── */
  const [autoAssigning, setAutoAssigning] = useState(false);
  const handleProfesorAutoAssign = useCallback(async () => {
    if (!reservaEff?.id) return;
    try {
      setAutoAssigning(true);
      await api.post(`/reservas/${(reservaEff as any)?.id}/aula/auto`, { force: true });
      enqueueSnackbar('Aula asignada automáticamente', { variant: 'success' });
      // refrescar planner + detalle
      refetchPlan?.();
      fetchReservaFresh();
      // disparar evento para otros componentes
      window.dispatchEvent(
        new CustomEvent('reserva:updated', { detail: { id: (reservaEff as any)?.id } }),
      );
    } catch (e: any) {
      const msg = e?.response?.data?.message ?? e?.message ?? 'No se pudo auto-asignar un aula';
      enqueueSnackbar(String(msg), { variant: 'error' });
    } finally {
      setAutoAssigning(false);
    }
  }, [reservaEff?.id, refetchPlan, fetchReservaFresh, enqueueSnackbar]);

  /* ─────────── IMPORTANTE: el return va DESPUÉS de todos los hooks ─────────── */
  if (!reserva) return null;

  /* ═════════════════════ render ═════════════════════ */
  return (
    <Dialog
      key={(reservaEff as any)?.id ?? 'none'}
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Trans i18nKey="reservaDialog.title" values={{ id: (reservaEff as any)?.id }} />
      </DialogTitle>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          px: 2,
          '& .MuiTab-root.Mui-selected': { color: '#21a598' },
          '& .MuiTabs-indicator': { backgroundColor: '#21a598' },
        }}
      >
        <Tab label={t('reservaDialog.tabs.details')} />
        {isStaff && <Tab label={t('reservaDialog.tabs.history')} />}
      </Tabs>

      <DialogContent dividers sx={{ minHeight: 300 }}>
        {tab === 0 && (
          <Stack spacing={1.5}>
            {/* ───────── Cabecera ───────── */}
            <Stack spacing={1.5} width="100%">
              <Box
                sx={{
                  position: 'relative',
                  p: 2.5,
                  borderRadius: 3,
                  backgroundColor: '#fcf1ad',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                  minWidth: 0,
                }}
              >
                {isProfesor && !!taUrl && (
                  <Box sx={{ position: 'absolute', top: 8, right: 12 }}>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<LinkIcon />}
                      disabled={taLoading}
                      onClick={() => taUrl && window.open(taUrl, '_blank', 'noopener,noreferrer')}
                      sx={{
                        backgroundColor: '#21a598',
                        '&:hover': { backgroundColor: '#1a8578' },
                        boxShadow: 'none',
                      }}
                    >
                      Link Campus
                    </Button>
                  </Box>
                )}

                <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                  <Box
                    component="img"
                    src={
                      (reservaEff as any)?.idCentro === 1
                        ? moralejaImg
                        : (reservaEff as any)?.idCentro === 2
                          ? pozueloImg
                          : (reservaEff as any)?.idCentro === 3
                            ? plazacastillaImg
                            : otherImg
                    }
                    alt={(reservaEff as any)?.nombreCentro || 'Centro'}
                    sx={{
                      width: 100,
                      height: 100,
                      borderRadius: 1,
                      objectFit: 'cover',
                      flexShrink: 0,
                    }}
                  />

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 700, color: '#8b6d1f', mb: 1 }}
                    >
                      {(reservaEff as any)?.nombreProyecto ??
                        ((reservaEff as any)?.idProyecto
                          ? dic.proy[(reservaEff as any).idProyecto]
                          : (reservaEff as any)?.proyecto)}{' '}
                      – {(reservaEff as any)?.nombreAsignatura}
                    </Typography>

                    <Typography variant="body2">
                      <strong>Centro:</strong> {(reservaEff as any)?.nombreCentro}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Curso:</strong> {(reservaEff as any)?.studentYear}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Examination Board:</strong> {(reservaEff as any)?.examBoard}
                    </Typography>
                  </Box>
                </Box>

                {(reservaEff as any)?.nombreFormato && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 8,
                      right: 12,
                      bgcolor: '#21a598',
                      color: '#fff',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: '999px',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                    }}
                  >
                    {(reservaEff as any)?.nombreFormato}
                  </Box>
                )}
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mt: 0,
                  mb: 2,
                }}
              >
                <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#555' }}>
                  Firmas: {firmadas} / {(reservaEff as any)?.firmasRequeridas ?? '—'}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#555' }}>
                  {estadoLegible}
                </Typography>
              </Box>

              {/* ───────── Detalles ───────── */}
              <Stack spacing={0.5}>
                {/* Fila del profesor con recuadro y botón Notificar retraso */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    p: 1.25,
                    border: '1px solid #21a598',
                    borderRadius: '6px',
                    mt: 1,
                    mb: 4,
                  }}
                >
                  {/* primera fila: nombre del profesor y botón de retraso */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Typography variant="body2">
                      <strong>{t('reservaDialog.field.professor')}:</strong>{' '}
                      {(reservaEff as any)?.nombreProf}
                    </Typography>
                    {/* Profesor secundario (shadowing) */}
                    {((reservaEff as any)?.shadowingInfo?.profesor ||
                      (reservaEff as any)?.shadowing?.profesor) && (
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        <strong>
                          {t('reservaDialog.field.shadowingTeacher', 'Profesor de Shadowing')}:
                        </strong>{' '}
                        {(
                          (reservaEff as any)?.shadowingInfo?.profesor ??
                          (reservaEff as any)?.shadowing?.profesor
                        ).nombre
                          ? `${((reservaEff as any)?.shadowingInfo?.profesor ?? (reservaEff as any)?.shadowing?.profesor).nombre} ${((reservaEff as any)?.shadowingInfo?.profesor ?? (reservaEff as any)?.shadowing?.profesor).apellido}`.trim()
                          : ((
                              (reservaEff as any)?.shadowingInfo?.profesor ??
                              (reservaEff as any)?.shadowing?.profesor
                            ).nombreCompleto ??
                            `#${((reservaEff as any)?.shadowingInfo?.profesor ?? (reservaEff as any)?.shadowing?.profesor).id}`)}
                      </Typography>
                    )}

                    <Box
                      sx={{
                        position: 'relative',
                        display: 'inline-flex',
                        alignItems: 'center',
                        '& .MuiButton-root': {
                          position: 'relative',
                          pl: 4.5,
                          color: '#21a598',
                          borderColor: '#21a598',
                          textTransform: 'none',
                          '&:hover': {
                            backgroundColor: 'rgba(33,165,152,0.08)',
                            borderColor: '#1b887f',
                            color: '#1b887f',
                          },
                          '& .MuiSvgIcon-root': {
                            display: 'none !important',
                          },
                        },
                        '& .custom-clock-icon': {
                          position: 'absolute',
                          left: 10,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          pointerEvents: 'none',
                          color: '#21a598',
                          fontSize: 18,
                        },
                      }}
                    >
                      <AccessTimeIcon
                        className="custom-clock-icon"
                        sx={{ opacity: canTeacherDelay ? 1 : 0.5 }}
                      />
                      <span
                        style={{
                          display: 'inline-block',
                          width: '100%',
                          opacity: canTeacherDelay ? 1 : 0.5,
                          pointerEvents: canTeacherDelay ? 'auto' : 'none',
                        }}
                      >
                        <TeacherDelayButton
                          reservaId={(reservaEff as any)?.id}
                          buttonText="Notificar retraso"
                          variant="outlined"
                          size="small"
                        />
                      </span>
                    </Box>
                  </Box>

                  {isProfesor && faltan > 0 && !yaFirmo && (
                    <Tooltip
                      title={!hasStarted ? 'Disponible cuando comience la clase' : undefined}
                      disableHoverListener={hasStarted} // No mostrar tooltip cuando está habilitado
                    >
                      <span style={{ display: 'inline-block', width: '100%' }}>
                        <Button
                          fullWidth
                          size="small"
                          variant="contained"
                          onClick={() => setDlgFirma(true)}
                          disabled={!hasStarted} // <- ya no depende de inStart20Window
                          sx={{
                            mt: 1,
                            backgroundColor: hasStarted ? '#21a598' : 'grey.500',
                            color: '#fff',
                            textTransform: 'uppercase',
                            fontWeight: 400,
                            borderRadius: '4px',
                            boxShadow: 'none',
                            '&:hover': {
                              backgroundColor: hasStarted ? '#1b887f' : 'grey.500',
                              boxShadow: 'none',
                            },
                          }}
                        >
                          {t('reservaDialog.btn.sign')}
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </Box>

                <Typography variant="body2">
                  <strong>{t('reservaDialog.field.students')}:</strong>{' '}
                  {alumnosNombres.length
                    ? alumnosNombres.join(', ')
                    : t('reservaDialog.noAssignedStudent')}
                </Typography>
                {ocupacion && (
                  <Typography variant="body2">
                    <strong>{t('reservaDialog.field.occupation')}:</strong> {ocupacion}
                  </Typography>
                )}

                {/* ← AULA: chip + botón "Solicitar aula" (auto-asignación) si el formato es Online */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                  }}
                >
                  <Chip
                    label={`Aula: ${aulaNombreEfectivo}${aula?.zona ? ` (${aula.zona})` : ''}`}
                    sx={{
                      bgcolor: '#21a598',
                      color: '#fff',
                      borderRadius: '999px',
                      height: 24,
                      '& .MuiChip-label': { px: 1.25, fontSize: '0.8125rem' },
                    }}
                  />
                  {aula && typeof (aula as any).capacidad !== 'undefined' && (
                    <Typography variant="body2" component="span">
                      · {(aula as any).capacidad} plazas
                    </Typography>
                  )}

                  {isProfesor && isFormatoOnline && (
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={autoAssigning}
                      onClick={handleProfesorAutoAssign}
                      sx={{
                        ml: 0.5,
                        color: '#21a598',
                        borderColor: '#21a598',
                        textTransform: 'none',
                        '&:hover': {
                          backgroundColor: 'rgba(33,165,152,0.08)',
                          borderColor: '#1b887f',
                        },
                      }}
                    >
                      {autoAssigning ? 'Asignando…' : 'Solicitar aula'}
                    </Button>
                  )}
                </Box>

                <Typography variant="body2">
                  <strong>{t('reservaDialog.field.dateTime')}:</strong>{' '}
                  {fmtFecha(
                    (reservaEff as any)?.fecha,
                    (reservaEff as any)?.horaInicio,
                    (reservaEff as any)?.horaFin,
                  )}
                </Typography>
                <Typography variant="body2">
                  <strong>{t('reservaDialog.field.language')}:</strong>{' '}
                  {(reservaEff as any)?.language}
                </Typography>
                <Typography variant="body2">
                  <strong>{t('reservaDialog.field.link')}:</strong> {(reservaEff as any)?.link}
                </Typography>
                <Typography variant="body2">
                  <strong>{t('reservaDialog.field.comments')}:</strong>{' '}
                  {(reservaEff as any)?.comentarios}
                </Typography>
                {showInternos && (
                  <Typography variant="body2">
                    <strong>{t('reservaDialog.field.internalComments')}:</strong>{' '}
                    {(reservaEff as any)?.comentariosInternos}
                  </Typography>
                )}
              </Stack>
            </Stack>

            {/* ───────── Firma rápida si falta (evitar duplicado con el recuadro del profe) ───────── */}
            {faltan > 0 && !yaFirmo && !isProfesor && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => setDlgFirma(true)}
                sx={{
                  color: '#21a598',
                  borderColor: '#21a598',
                  textTransform: 'uppercase',
                  fontWeight: 400,
                  boxShadow: 'none',
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: 'rgba(33,165,152,0.08)',
                    borderColor: '#1b887f',
                    boxShadow: 'none',
                  },
                }}
              >
                {t('reservaDialog.btn.sign')}
              </Button>
            )}

            {/* ───────── Incidencias del profesor (NO profesor) ───────── */}
            {!isProfesor && (
              <Box
                sx={{
                  mt: 1.5,
                  p: 1.5,
                  border: '1px dashed #ccc',
                  borderRadius: 1,
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Incidencias del/a profesor/a
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Box sx={{ color: '#21a598' }}>
                    <StaffDelayButton
                      reservaId={(reservaEff as any)?.id}
                      buttonText="Retraso del profesor"
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                  <Box sx={{ color: '#21a598' }}>
                    <TeacherAbsenceButton
                      reservaId={(reservaEff as any)?.id}
                      buttonText="Ausencia profesor"
                      variant="outlined"
                      color="inherit"
                      size="small"
                    />
                  </Box>
                </Stack>
              </Box>
            )}

            {/* ───────── Acciones por alumno (solo PROFESOR) ───────── */}
            {isProfesor && alumnosIdsEffective.length > 0 && (
              <Box
                sx={{
                  mt: 1.5,
                  p: 1.5,
                  border: '1px dashed #ccc',
                  borderRadius: 1,
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Enlaces de firmas del alumnado
                </Typography>

                <Stack spacing={1}>
                  {alumnosIdsEffective.map((id) => {
                    const nombre = dic.cliente[id] ?? `#${id}`;
                    const firmado = firmas.some(
                      (f: any) => f.rolUsuario === 'alumno' && f.idUsuario === id,
                    );

                    const disabledSign = firmado || !!loadingGen[id] || !canSignForAlumno(id);
                    const disabledForm = !!loadingForm[id] || !canFormForAlumno(id);

                    const signTip = !hasStarted
                      ? 'Disponible al inicio de la clase'
                      : canSignForAlumno(id)
                        ? 'Firma disponible (20 min desde el inicio o 30 min tras retraso)'
                        : 'Fuera de ventana de firma';

                    const formTip = !hasStarted
                      ? 'El formulario estará disponible una vez iniciada la clase'
                      : !canFormForAlumno(id)
                        ? 'No disponible mientras la firma está activa'
                        : undefined;

                    return (
                      <Stack
                        key={id}
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        flexWrap="wrap"
                      >
                        <Typography sx={{ flexGrow: 1, minWidth: 160 }}>{nombre}</Typography>
                        <Chip
                          size="small"
                          label={firmado ? 'Firmado' : 'Pendiente'}
                          color={firmado ? 'success' : 'warning'}
                        />

                        {/* Retraso del alumno */}
                        <Tooltip
                          title={!hasStarted ? 'Disponible cuando comience la clase' : undefined}
                        >
                          <Box
                            sx={{
                              position: 'relative',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minWidth: 0,
                              opacity: hasStarted ? 1 : 0.5,
                              // estilos del botón como ya tienes...
                              '& .MuiButton-root': {
                                minWidth: 36,
                                height: 34,
                                px: 1.25,
                                py: 0.8,
                                pl: 1.5,
                                fontSize: '0.8125rem',
                                textTransform: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#21a598',
                                borderColor: '#21a598',
                                '&:hover': {
                                  backgroundColor: 'rgba(33,165,152,0.08)',
                                  borderColor: '#1b887f',
                                },
                                '& .MuiButton-startIcon': {
                                  margin: 0,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '100%',
                                  '& svg': {
                                    display: 'block',
                                    margin: '0 auto',
                                    fontSize: '1.25rem',
                                    color: '#21a598',
                                  },
                                },
                              },
                            }}
                            aria-disabled={!hasStarted}
                            tabIndex={hasStarted ? 0 : -1}
                          >
                            {/* Overlay que bloquea clics/teclado antes de empezar */}
                            {!hasStarted && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  inset: 0,
                                  zIndex: 1,
                                  cursor: 'not-allowed',
                                  // importante: capturar eventos para bloquear
                                  pointerEvents: 'auto',
                                }}
                              />
                            )}

                            <StudentDelayButton
                              reservaId={(reservaEff as any)?.id}
                              studentId={id}
                              buttonText=""
                              variant="outlined"
                              size="small"
                              // SIN disabled
                            />
                          </Box>
                        </Tooltip>

                        {/* Ausencia del alumno */}
                        <Tooltip
                          title={!hasStarted ? 'Disponible cuando comience la clase' : undefined}
                        >
                          <Box
                            sx={{
                              position: 'relative',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minWidth: 0,
                              opacity: hasStarted ? 1 : 0.5,
                              '& .MuiButton-root': {
                                minWidth: 0,
                                px: 1.25,
                                py: 0.8,
                                pl: 1.5,
                                fontSize: '0.8125rem',
                                textTransform: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#21a598',
                                borderColor: '#21a598',
                                '&:hover': {
                                  backgroundColor: 'rgba(33,165,152,0.08)',
                                  borderColor: '#1b887f',
                                },
                                '& .MuiButton-startIcon': {
                                  margin: 0,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '100%',
                                  '& svg': { display: 'none' },
                                },
                              },
                            }}
                            aria-disabled={!hasStarted}
                            tabIndex={hasStarted ? 0 : -1}
                          >
                            {!hasStarted && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  inset: 0,
                                  zIndex: 1,
                                  cursor: 'not-allowed',
                                  pointerEvents: 'auto',
                                }}
                              />
                            )}

                            <StudentAbsenceButton
                              reservaId={(reservaEff as any)?.id}
                              studentId={id}
                              buttonText=""
                              variant="outlined"
                              size="small"
                              // SIN disabled
                            />
                          </Box>
                        </Tooltip>
                        {/* Copiar enlace */}
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<ContentCopy />}
                          disabled={!linksByAlumno[id]}
                          onClick={() => copiarEnlaceAlumno(id)}
                          sx={{
                            minWidth: 0,
                            px: 1.25,
                            py: 0.8,
                            pl: 1.5,
                            fontSize: '0.8125rem',
                            textTransform: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: !linksByAlumno[id] ? 'grey.500' : '#21a598',
                            borderColor: !linksByAlumno[id] ? 'grey.300' : '#21a598',
                            '&:hover': {
                              backgroundColor: !linksByAlumno[id]
                                ? 'transparent'
                                : 'rgba(33,165,152,0.08)',
                              borderColor: !linksByAlumno[id] ? 'grey.300' : '#1b887f',
                            },
                            '& .MuiButton-startIcon': {
                              marginRight: 0,
                              display: 'flex',
                              alignItems: 'center',
                            },
                          }}
                        />

                        {/* Enlace firma */}
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<LinkIcon />}
                          disabled={disabledSign}
                          title={signTip}
                          onClick={() => generarEnlaceAlumno(id)}
                          sx={{
                            px: 1.25,
                            py: 0.5,
                            fontSize: '0.8125rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: disabledSign ? 'grey.500' : '#21a598',
                            borderColor: disabledSign ? 'grey.300' : '#21a598',
                            '&:hover': {
                              backgroundColor: disabledSign
                                ? 'transparent'
                                : 'rgba(33,165,152,0.08)',
                              borderColor: disabledSign ? 'grey.300' : '#1b887f',
                            },
                          }}
                        >
                          {loadingGen[id] ? 'Generando…' : 'Enlace firma'}
                        </Button>

                        {/* Copiar formulario */}
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<ContentCopy />}
                          disabled={!formLinksByAlumno[id]}
                          onClick={() => copiarFormularioAlumno(id)}
                          sx={{
                            minWidth: 0,
                            px: 1.25,
                            py: 0.8,
                            pl: 1.5,
                            fontSize: '0.8125rem',
                            textTransform: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: !formLinksByAlumno[id] ? 'grey.500' : '#21a598',
                            borderColor: !formLinksByAlumno[id] ? 'grey.300' : '#21a598',
                            '&:hover': {
                              backgroundColor: !formLinksByAlumno[id]
                                ? 'transparent'
                                : 'rgba(33,165,152,0.08)',
                              borderColor: !formLinksByAlumno[id] ? 'grey.300' : '#1b887f',
                            },
                            '& .MuiButton-startIcon': {
                              marginRight: 0,
                              display: 'flex',
                              alignItems: 'center',
                            },
                          }}
                        />

                        {/* Formulario */}
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<LinkIcon />}
                          disabled={disabledForm}
                          title={formTip}
                          onClick={() => generarFormularioAlumno(id)}
                          sx={{
                            px: 1.25,
                            py: 0.5,
                            fontSize: '0.8125rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: disabledForm ? 'grey.500' : '#21a598',
                            borderColor: disabledForm ? 'grey.300' : '#21a598',
                            '&:hover': {
                              backgroundColor: disabledForm
                                ? 'transparent'
                                : 'rgba(33,165,152,0.08)',
                              borderColor: disabledForm ? 'grey.300' : '#1b887f',
                            },
                          }}
                        >
                          {loadingForm[id] ? 'Generando…' : 'Formulario'}
                        </Button>
                      </Stack>
                    );
                  })}
                </Stack>
              </Box>
            )}

            {isProfesor && (
              <OneMinutePaperSection open={open} reservaId={(reservaEff as any)?.id} />
            )}
          </Stack>
        )}

        {isStaff &&
          tab === 1 &&
          (isFetching ? (
            <CircularProgress />
          ) : logs.length === 0 ? (
            <Typography>{t('reservaDialog.msg.noChanges')}</Typography>
          ) : (
            <Timeline logs={logs} dic={dic} showInternos={isStaff} />
          ))}
      </DialogContent>

      <DialogActions
        sx={{
          paddingRight: '25px',
          paddingBottom: '15px',
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            color: '#21a598',
            borderColor: '#21a598',
            '&:hover': {
              borderColor: '#21a598',
              backgroundColor: 'rgba(33,165,152,0.08)',
            },
          }}
        >
          {t('common.close')}
        </Button>

        {isStaff && titularUsrId && (
          <Button
            variant="outlined"
            startIcon={loadingCli ? <CircularProgress size={18} /> : <PersonIcon />}
            sx={{
              color: '#21a598',
              borderColor: '#21a598',
              '&:hover': {
                borderColor: '#21a598',
                backgroundColor: 'rgba(33,165,152,0.08)',
              },
            }}
            onClick={async () => {
              const titularCache = clientes.find((c) => c.id === titularUsrId);
              if (titularCache) {
                setClienteSel(titularCache);
                return;
              }
              try {
                setLoadingCli(true);
                const { data } = await api.get<Cliente>(`/usuarios/${titularUsrId}`);
                setClienteSel(data);
              } catch (e) {
                // No se pudo cargar el cliente
              } finally {
                setLoadingCli(false);
              }
            }}
          >
            {t('reservaDialog.btn.viewClient')}
          </Button>
        )}

        {isConsultor && (
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            sx={{
              color: '#21a598',
              borderColor: '#21a598',
              '&:hover': {
                borderColor: '#21a598',
                backgroundColor: 'rgba(33,165,152,0.08)',
              },
            }}
            onClick={() => setDlgCancel(true)}
          >
            {t('reservaDialog.btn.cancelIssue')}
          </Button>
        )}

        {isStaff && !isConsultor && onDuplicate && (
          <Button
            variant="outlined"
            startIcon={<ContentCopy />}
            sx={{
              color: '#21a598',
              borderColor: '#21a598',
              '&:hover': {
                borderColor: '#21a598',
                backgroundColor: 'rgba(33,165,152,0.08)',
              },
            }}
            onClick={() => {
              onClose();
              setTimeout(() => {
                onDuplicate(reservaEff);
              }, 0);
            }}
          >
            {t('reservaDialog.btn.duplicate')}
          </Button>
        )}

        {isStaff && !isConsultor && onEdit && (
          <Button
            variant="contained"
            startIcon={<Edit />}
            sx={{
              backgroundColor: '#21a598',
              '&:hover': { backgroundColor: '#1a8578' },
              boxShadow: 'none',
            }}
            onClick={async () => {
              try {
                const { data } = await api.get(`/reservas/${(reservaEff as any)?.id}`);
                onClose();
                setTimeout(() => {
                  onEdit({ ...reservaEff, ...data });
                }, 0);
              } catch (e) {
                /* silencioso */
              }
            }}
          >
            {t('reservaDialog.btn.edit')}
          </Button>
        )}
      </DialogActions>

      {/* diálogos secundarios */}
      <FirmaDialog
        open={dlgFirma}
        reservaId={(reservaEff as any)?.id}
        onClose={() => setDlgFirma(false)}
      />

      {isConsultor && (
        <CancelDialog
          open={dlgCancel}
          reservaId={(reservaEff as any)?.id}
          onClose={() => setDlgCancel(false)}
          onSaved={() => {
            setDlgCancel(false);
            onClose();
          }}
        />
      )}

      {clienteSel && (
        <ClienteDialog
          open
          initial={clienteSel}
          readOnly={isConsultor}
          onClose={() => setClienteSel(null)}
          onSave={
            isConsultor
              ? undefined
              : async (d) => {
                  await update(clienteSel.id, d);
                  setClienteSel(null);
                }
          }
        />
      )}
    </Dialog>
  );
}

/* ───────── Timeline (historial) ───────── */
function Timeline({ logs, dic, showInternos }: { logs: any[]; dic: Dic; showInternos: boolean }) {
  const { t } = useTranslation<'translation'>();

  function describe(op: any, prev: any, next: any): string | null {
    if (op.path === '') return null;
    const k = op.path.slice(1);
    if (!showInternos && k === 'comentariosInternos') return null;

    switch (k) {
      case 'idCentro':
        return t('reservaDialog.timeline.center', {
          old: dic.centro[prev.idCentro] ?? prev.idCentro,
          new: dic.centro[next.idCentro] ?? next.idCentro,
        });
      case 'idCliente':
        return t('reservaDialog.timeline.ownerStudent', {
          old: dic.cliente[prev.idCliente] ?? prev.idCliente,
          new: dic.cliente[next.idCliente] ?? next.idCliente,
        });
      case 'idAsignatura':
        return t('reservaDialog.timeline.subject', {
          old: dic.asign[prev.idAsignatura] ?? prev.idAsignatura,
          new: dic.asign[next.idAsignatura] ?? next.idAsignatura,
        });
      case 'idFormato':
        return t('reservaDialog.timeline.format', {
          old: dic.formato[prev.idFormato] ?? prev.idFormato,
          new: dic.formato[next.idFormato] ?? next.idFormato,
        });
      case 'examinationBoard':
        return t('reservaDialog.timeline.board', {
          old: prev.examinationBoard ?? '—',
          new: next.examinationBoard ?? '—',
        });
      case 'linkAddress':
        return t('reservaDialog.timeline.link', {
          old: prev.linkAddress ?? '—',
          new: next.linkAddress ?? '—',
        });
      case 'curso':
        return t('reservaDialog.timeline.year', {
          old: prev.curso ?? '—',
          new: next.curso ?? '—',
        });
      case 'language':
        return t('reservaDialog.timeline.language', {
          old: prev.language ?? '—',
          new: next.language ?? '—',
        });
      case 'proyecto':
        return t('reservaDialog.timeline.project', {
          old: prev.proyecto ?? '—',
          new: next.proyecto ?? '—',
        });
      case 'idProyecto':
        return t('reservaDialog.timeline.project', {
          old: (prev.idProyecto && dic.proy[prev.idProyecto]) ?? prev.idProyecto ?? '—',
          new: (next.idProyecto && dic.proy[next.idProyecto]) ?? next.idProyecto ?? '—',
        });
      case 'estado':
        return t('reservaDialog.timeline.status', {
          old: prev.estado ?? '—',
          new: next.estado ?? '—',
        });
      case 'numAlumnos':
        return t('reservaDialog.timeline.places', {
          old: prev.numAlumnos,
          new: next.numAlumnos,
        });
      case 'alumnosIds':
        return t('reservaDialog.timeline.participants');
      case 'comentariosInternos':
        return showInternos ? t('reservaDialog.timeline.internalComments') : null;
      default: {
        const fmt = (v: unknown) =>
          typeof v === 'object' ? JSON.stringify(v) : ((v as any) ?? '—');
        return `${ucFirst(k)}: ${fmt(prev[k])} → ${fmt(next[k])}`;
      }
    }
  }

  return (
    <Stack spacing={2}>
      {logs.map((l: any) => {
        const frases = (l.diff ?? [])
          .map((op: any) => describe(op, l.snapshotOld ?? {}, l.snapshotNew ?? {}))
          .filter(Boolean) as string[];
        if (l.accion === 'CANCEL' && frases.length === 0)
          frases.unshift(t('reservaDialog.timeline.cancelled'));

        return (
          <Box key={l.id} sx={{ p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" sx={{ flexGrow: 1 }}>
                {dayjs(l.fecha).format('DD/MM/YYYY HH:mm')} — {l.actor?.nombreCompleto ?? '—'}
              </Typography>
              <Chip
                size="small"
                label={l.accion}
                color={
                  l.accion === 'CREATE'
                    ? 'success'
                    : l.accion === 'DELETE'
                      ? 'error'
                      : l.accion === 'CANCEL'
                        ? 'warning'
                        : 'info'
                }
              />
            </Stack>

            {frases.length > 0 && (
              <List dense sx={{ pl: 1.5 }}>
                {frases.map((txt, i) => (
                  <ListItem key={i} disableGutters>
                    <ListItemText primary={txt} primaryTypographyProps={{ fontSize: 14 }} />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        );
      })}
    </Stack>
  );
}

/* util */
const ucFirst = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
